import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@crowdex/ui/ai/conversation';
import { Message, MessageContent } from '@crowdex/ui/ai/message';
import { DesktopChatEmptyState } from '@/components/desktop-chat-empty-state';
import { DesktopMessageList } from '@/components/desktop-message-list';
import { TurnWorking } from '@/components/desktop-message-part/turn-steps';
import { useConversationCards } from '@/features/apps/conversation-cards';
import { useArtifactIndex } from '@/features/apps/use-artifact-index';
import { useDesktopPreviewState } from '@/lib/desktop-preview-state';
import { whileConversationShown } from '@/lib/desktop-shell/conversation-scope';
import type { MessageChangeControls } from '@/lib/desktop-shell/message-change-controls';
import { dockArtifact } from '@/lib/desktop-shell/open-artifact';
import { useResumedTranscript } from '@/lib/desktop-shell/pane-view-state';
import type { PendingAnnotation } from '@/lib/desktop-shell/store';
import { useDesktopShellStore, useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import type { NativeAgent } from '@/lib/native-desktop';
import { showPanelView } from '@/lib/side-panel/panel-tabs';

const UUID = /^[0-9a-f-]{36}$/i;

/**
 * The scrolling conversation pane: what is missing before a conversation can start
 * (no agent yet; a new conversation itself is the pane's start screen), otherwise the message transcript plus the
 * submitted/streaming "working" status bubble. Purely view-layer — the workspace
 * name, discovery flag, runtime, editing container, and side-panel view toggles
 * are read from the store, while the conversation/connection-derived data and
 * the message actions (edit, plan, debug, annotate, review,
 * undo, subagent) arrive as props since they are owned by `DesktopShell`.
 */
export function ConversationMessages({
  agent,
  displayedMessages,
  messages,
  status: chatStatus,
  onRefreshAgents,
  onEditMessage,
  onOpenSubagent,
  onAnnotateSelection,
  onAskSelectionInSideChat,
  onLoadMessageDiff,
  onUndoMessageChanges,
}: {
  agent: NativeAgent | null;
  displayedMessages: readonly CrowdexUIMessage[];
  messages: readonly CrowdexUIMessage[];
  status: 'submitted' | 'streaming' | 'ready' | 'error';
  onRefreshAgents: () => void;
  onEditMessage: ((message: CrowdexUIMessage, text: string) => void) | undefined;
  onBuildPlan?: (() => void) | undefined;
  onOpenSubagent: (toolCallId: string) => void;
  onAnnotateSelection: (selection: PendingAnnotation) => void;
  onAskSelectionInSideChat: (content: string, autoSubmit?: boolean) => void;
  onLoadMessageDiff: ((message: CrowdexUIMessage, path?: string) => Promise<string>) | undefined;
  onUndoMessageChanges: MessageChangeControls['undoMessageChanges'] | undefined;
}) {
  const store = useDesktopShellStoreApi();
  // A preview board's turn may still run (preview only; empty everywhere else).
  const previewStatus = useDesktopPreviewState((state) => state.chatStatus);
  const status = previewStatus ?? chatStatus;
  const chatId = useDesktopShellStore((state) => state.chatId);
  // Made the active one in a split, it is drawn again where it was read.
  const resumed = useResumedTranscript(chatId);
  // Shared with the side panel, which reads them again when a turn ends.
  const { artifacts } = useArtifactIndex(UUID.test(chatId) ? chatId : null);
  // Read again as messages come and turns end: a run's steps put theirs between turns.
  const cards = useConversationCards(
    UUID.test(chatId) ? chatId : null,
    `${displayedMessages.length}:${status}`,
  );
  const native = useDesktopShellStore((state) => state.native);
  const discoveringAgents = useDesktopShellStore((state) => state.discoveringAgents);
  const workspaceSyncError = useDesktopShellStore((state) => state.workspaceSyncError);
  const editingMessageId = useDesktopShellStore((state) => state.editingMessageId);
  const setEditingMessageContainer = useDesktopShellStore(
    (state) => state.setEditingMessageContainer,
  );
  const setSidePanelOpen = useDesktopShellStore((state) => state.setSidePanelOpen);
  const setPanelTabs = useDesktopShellStore((state) => state.setPanelTabs);
  const setAgentTerminalRequestId = useDesktopShellStore(
    (state) => state.setAgentTerminalRequestId,
  );

  // A conversation keeps showing its transcript when its folder or agent is gone: the notices
  // above say what is missing.
  const empty = displayedMessages.length === 0;

  return (
    <Conversation className="min-h-0 flex-1" {...resumed}>
      <ConversationContent
        className="mx-auto min-h-full w-full max-w-conversation translate-x-(--thread-content-shift,0px) gap-6 px-5 py-7 transition-[translate] duration-500 ease-out motion-reduce:transition-none sm:px-7"
        scrollClassName="overflow-x-hidden [overflow-anchor:none]"
      >
        {empty ? (
          <DesktopChatEmptyState
            agentName={agent?.name ?? null}
            discoveringAgents={discoveringAgents}
            onRefreshAgents={onRefreshAgents}
            runtime={native === false ? 'cloud' : 'local'}
            syncFailed={workspaceSyncError !== null}
          />
        ) : (
          <>
            <DesktopMessageList
              editingMessageId={editingMessageId}
              messages={displayedMessages}
              streamingMessageId={
                status === 'streaming' && messages.at(-1)?.role === 'assistant'
                  ? messages.at(-1)?.id
                  : undefined
              }
              onEditContainerChange={setEditingMessageContainer}
              onEditMessage={onEditMessage}
              onOpenPlan={() => {
                setPanelTabs((tabs) => showPanelView(tabs, 'plan'));
                setSidePanelOpen(true);
              }}
              onOpenSubagent={onOpenSubagent}
              onOpenTerminal={(terminalId) => {
                setAgentTerminalRequestId(terminalId);
                setSidePanelOpen(true);
              }}
              onAnnotateMessage={(message, text, anchor) =>
                onAnnotateSelection({
                  anchor,
                  content: text,
                  sourceId: message.id,
                  sourceKind: 'chat',
                  sourceLabel: `${message.role === 'user' ? 'User' : 'Agent'} message`,
                })
              }
              onAskMessageSelectionInSideChat={(_message, text) => onAskSelectionInSideChat(text)}
              onMoreDetailsMessageSelection={(_message, text) =>
                onAskSelectionInSideChat(text, true)
              }
              onReviewMessageChanges={(diff) =>
                // Read once the user left the conversation, its diff does not open beside another.
                whileConversationShown(store, chatId, (state) => {
                  state.setMessageReviewDiff(diff);
                  state.setPanelTabs((tabs) => showPanelView(tabs, 'review'));
                  state.setSidePanelOpen(true);
                })
              }
              onLoadMessageDiff={onLoadMessageDiff}
              onUndoMessageChanges={onUndoMessageChanges}
              artifacts={artifacts}
              // Lot 5b: an artifact opens in a widened panel (D15).
              onOpenArtifact={(artifact) => dockArtifact(store, artifact.id)}
              cards={cards}
              conversation={UUID.test(chatId) ? chatId : null}
              withActions
            />
            {(status === 'submitted' ||
              (status === 'streaming' && messages.at(-1)?.role !== 'assistant')) && (
              <Message className="desktop-message" from="assistant">
                <MessageContent>
                  <TurnWorking />
                </MessageContent>
              </Message>
            )}
          </>
        )}
      </ConversationContent>
      <ConversationScrollButton className="bottom-3 size-8 border-border bg-card shadow-lg" />
    </Conversation>
  );
}
